/**
 * 山形県の背景ツアー「山形めぐり」コンテンツ。
 *
 * 軸は北海道と同じく「季節」。5月の羽黒山から始めて、
 * 1年かけて翌年4月の白川湖へ戻る。春=朝 → 夏=昼 → 秋=夕 → 冬=夜 で、
 * 画面が徐々に暗くなる演出は福岡・北海道と同じまま保っている。
 *
 * 「書いてはいけないこと」（claude/山形データシート案.md）を反映している。
 * とくに次は書いていない。
 *  - 「世界三大舟唄」「世界三大樹氷」（どちらも選定主体のない俗称）
 *  - 「西の堺、東の酒田」（国・県・市・観光協会のどこにも無い）
 *  - 銀山温泉が「千と千尋の神隠し」のモデルだという断定（公式は比喩表現のみ）
 *  - 平将門が五重塔を「建てた」という断定（公式はすべて「伝えられる」）
 *  - 山寺を「史跡」と書くこと（国の指定は「名勝」）
 */

import type { StageTour } from '../types';

export const yamagataSceneryTour: StageTour = {
  scenes: [
    {
      id: 'haguro',
      name: '羽黒山 五重塔',
      area: '鶴岡市・5月の朝',
      image: '/assets/scenery/yamagata/scenes/01_haguro.webp',
      hotspots: [
        {
          id: 'haguro-tou',
          x: 0.62,
          y: 0.22,
          title: '応安5年ごろの国宝の塔',
          body: '高さ約29メートル、屋根はこけら葺きの五重塔です。いまの塔は応安5年（1372年）ごろに建てられたもので、昭和41年（1966年）に国宝に指定されました。',
          source: 'https://online.bunka.go.jp/heritages/detail/199129',
          mapQuery: '羽黒山五重塔',
        },
        {
          id: 'haguro-ishidan',
          x: 0.22,
          y: 0.35,
          title: '2,446段の石段と33の彫り物',
          body: '山頂までの参道は2,446段。1648年の着工から13年かけて築かれました。石段には工事にあたった人々が彫ったとされる徳利や盃、瓢箪などが33個あり、すべて見つけると願いがかなうといわれます。歩きやすい靴で。',
          source: 'https://hagurokanko.jp/model/osusumecourse/',
          mapQuery: '出羽三山神社 羽黒山',
        },
      ],
    },
    {
      id: 'yamadera',
      name: '山寺 立石寺',
      area: '山形市・6月の午前',
      image: '/assets/scenery/yamagata/scenes/02_yamadera.webp',
      hotspots: [
        {
          id: 'yamadera-ishidan',
          x: 0.3,
          y: 0.36,
          title: '1,015段をのぼる',
          body: '貞観2年（860年）に慈覚大師円仁が開いた寺です。石段は全部で1,015段あり、往復の目安はおよそ1時間30分です。',
          source: 'https://rissyakuji.jp/',
          relatedItemId: 'tama-konnyaku',
          mapQuery: '宝珠山立石寺',
        },
        {
          id: 'yamadera-godaido',
          x: 0.52,
          y: 0.22,
          title: '五大堂',
          body: '開山から30年後に建てられた、五大明王をまつる道場です。断崖から突き出すように建っていて、山寺をひと目で見渡せます。',
          source: 'https://rissyakuji.jp/',
          mapQuery: '山寺 五大堂',
        },
        {
          id: 'yamadera-basho',
          x: 0.22,
          y: 0.46,
          title: '芭蕉が句を詠んだ地',
          body: '松尾芭蕉が「閑さや岩にしみ入る蝉の声」を詠んだ地として知られています。参道の途中にある「せみ塚」は、この句を書いた短冊を埋めて記念碑を建てたところです。',
          source: 'https://yamagatakanko.com/yamadera',
          mapQuery: '山寺 せみ塚',
        },
      ],
    },
    {
      id: 'mogamigawa',
      name: '最上川の舟下り',
      area: '戸沢村・7月の昼',
      image: '/assets/scenery/yamagata/scenes/03_mogamigawa.webp',
      hotspots: [
        {
          id: 'mogami-ikken',
          x: 0.2,
          y: 0.42,
          title: '一県だけを流れる大河',
          body: '最上川の長さは229キロメートル。県南の吾妻連峰に源を発し、日本海へ注ぎます。ひとつの県のなかだけを流れる川としては日本一の大河です。',
          source:
            'https://www.pref.yamagata.jp/020026/kensei/shoukai/yamagatamonogatari/shizen/mogamigawa.html',
          mapQuery: '最上峡芭蕉ライン観光',
        },
        {
          id: 'mogami-shiraito',
          x: 0.65,
          y: 0.2,
          title: '白糸の滝',
          body: '最上峡でいちばん大きな滝で、落差はおよそ120メートル。日本の滝百選のひとつです。舟に乗っているあいだが、いちばんよく見えるところ。',
          source: 'https://yamagatakanko.com/attractions/detail_11447.html',
          mapQuery: '白糸の滝 戸沢村',
        },
        {
          id: 'mogami-funauta',
          x: 0.38,
          y: 0.31,
          title: '番組がきっかけの舟唄',
          body: '「最上川舟唄」は、昭和11年（1936年）にNHK仙台放送局から最上川下りの番組で紹介する舟唄を求められたことをきっかけに、地元の人たちがまとめた民謡です。舟で働く人たちの掛け声などをもとに節がつくられました。',
          source: 'https://www.town.oe.yamagata.jp/kanko-event/kankou-bunka/40',
          mapQuery: '戸沢藩船番所',
        },
      ],
    },
    {
      id: 'rakan',
      name: '十六羅漢岩',
      area: '遊佐町・8月の昼過ぎ',
      image: '/assets/scenery/yamagata/scenes/04_rakan.webp',
      hotspots: [
        {
          id: 'rakan-22',
          x: 0.25,
          y: 0.2,
          title: '海辺の岩に22体',
          body: '吹浦の海禅寺の寛海和尚が、海の安全を願って造立を発願し、元治元年（1864年）から5年かけて彫り上げた磨崖仏です。十六羅漢に釈迦牟尼などを加えて、全部で22体あります。',
          source: 'https://www.yuzachokai.jp/spot/jurokurakaniwa/',
          relatedItemId: 'iwagaki',
          mapQuery: '十六羅漢岩',
        },
        {
          id: 'rakan-chokai',
          x: 0.72,
          y: 0.2,
          title: '鳥海山が流した岩',
          body: '仏が彫られているのは、鳥海山の噴火で日本海へ流れ出した安山岩です。背には鳥海山、前には日本海。夕方になると、岩のシルエットの向こうに陽が沈みます。',
          source: 'https://keikan.pref.yamagata.jp/vp_033/',
          mapQuery: '出羽二見 遊佐町',
        },
      ],
    },
    {
      id: 'sankyo',
      name: '山居倉庫',
      area: '酒田市・9月の午後',
      image: '/assets/scenery/yamagata/scenes/05_sankyo.webp',
      hotspots: [
        {
          id: 'sankyo-keyaki',
          x: 0.3,
          y: 0.09,
          title: 'ケヤキ並木は暑さよけ',
          body: '倉庫の裏に並ぶケヤキは、景色のためではなく、夏の高温から倉庫を守るために配されたものです。いまではCM撮影にも使われる、酒田でいちばん人気のフォトスポットになっています。',
          source: 'https://www.city.sakata.lg.jp/bunka/bunkazai/bunkazaishisetsu/sankyosouko.html',
          mapQuery: '山居倉庫 ケヤキ並木',
        },
        {
          id: 'sankyo-129',
          x: 0.18,
          y: 0.32,
          title: '129年ぶんの米',
          body: '明治26年（1893年）に建てられた米の保管倉庫。二重屋根で湿気を防ぐなど工夫が重ねられ、1万トン以上の米を収められました。2022年に米倉庫としての役目を終え、2021年に国の史跡になっています。',
          source: 'https://www.city.sakata.lg.jp/bunka/bunkazai/bunkazaishisetsu/sankyosouko.html',
          mapQuery: '山居倉庫',
        },
      ],
    },
    {
      id: 'uesugi',
      name: '上杉神社',
      area: '米沢市・10月の夕方',
      image: '/assets/scenery/yamagata/scenes/06_uesugi.webp',
      hotspots: [
        {
          id: 'uesugi-honmaru',
          x: 0.42,
          y: 0.22,
          title: '米沢城の本丸跡',
          body: '上杉謙信をまつる神社です。境内は旧米沢城の本丸跡で、およそ6,300坪。大正8年の米沢大火で焼けたあと、米沢出身の建築家・伊東忠太の設計で建て直されました。',
          source: 'https://uesugi-jinja.or.jp/history/',
          mapQuery: '上杉神社',
        },
        {
          id: 'uesugi-koi',
          x: 0.66,
          y: 0.45,
          title: '鷹山公と鯉',
          body: '米沢藩9代藩主・上杉鷹山が1802年、内陸で魚の乏しい米沢で滋養のある食べものを得られるようにと、鯉の養殖を勧めたのがはじまりとされます。いまも米沢の鯉のうま煮として受け継がれています。',
          source:
            'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/koinomani_yamagata.html',
          relatedItemId: 'koi-no-umani',
          mapQuery: '松岬神社 米沢',
        },
      ],
    },
    {
      id: 'ginzan',
      name: '銀山温泉',
      area: '尾花沢市・11月の日没後',
      image: '/assets/scenery/yamagata/scenes/07_ginzan.webp',
      hotspots: [
        {
          id: 'ginzan-gin',
          x: 0.3,
          y: 0.24,
          title: '近くにあった延沢銀山',
          body: '温泉の近くにある延沢銀山は、江戸時代を代表する銀山のひとつでした。元禄2年（1689年）に大崩落が起きて廃山になり、その跡はいま国の史跡に指定されています。',
          source: 'https://yamagatakanko.com/attractions/detail_829.html',
          mapQuery: '延沢銀山遺跡',
        },
        {
          id: 'ginzan-tatemono',
          x: 0.68,
          y: 0.3,
          title: '昭和元年に3〜4層の木造へ',
          body: '大正2年の銀山川の大洪水で、温泉宿のほとんどが流されました。昭和元年、源泉のボーリングで高温の湯が大量に湧くと、旅館は一斉に洋風の3〜4層の木造建築へ建て替えられました。',
          source: 'https://www.ginzanonsen.jp/ginzan/historyspa.html',
          mapQuery: '銀山温泉',
        },
        {
          id: 'ginzan-mycar',
          x: 0.46,
          y: 0.47,
          title: '冬は歩いて入る',
          body: '冬は積雪で道が狭くなり、共同駐車場も閉まります。緊急車両が通れるよう、尾花沢市は温泉街への車の乗り入れを控えるよう呼びかけています。日帰りなら手前の駐車場からシャトルバスで。',
          source: 'https://www.city.obanazawa.yamagata.jp/kanko/kankochi/1346',
          mapQuery: '銀山温泉 大正ろまん館',
        },
      ],
    },
    {
      id: 'zao',
      name: '蔵王の樹氷',
      area: '山形市・1月の夜',
      image: '/assets/scenery/yamagata/scenes/08_zao.webp',
      hotspots: [
        {
          id: 'zao-shikumi',
          x: 0.34,
          y: 0.28,
          title: '樹氷ができる三つの条件',
          body: 'シベリアからの季節風が過冷却水滴と雪を運ぶこと。氷や雪がつきやすいアオモリトドマツが生えていること。木が埋まりきらない適度な積雪。この三つがそろう厳冬期の山頂付近にだけできます。',
          source:
            'https://www.pref.yamagata.jp/020026/kensei/shoukai/yamagatamonogatari/shizen/juhyou.html',
          mapQuery: '蔵王ロープウェイ 地蔵山頂駅',
        },
        {
          id: 'zao-todomatsu',
          x: 0.5,
          y: 0.33,
          title: '樹氷をつくる木が減っている',
          body: '樹氷に欠かせないアオモリトドマツが、トウヒツヅリヒメハマキなどの虫の食害で次々と枯れています。令和元年度（2019年度）からは自生する苗を移す試みが始まり、令和3年には「アオモリトドマツ再生会議」もつくられました。',
          source:
            'https://www.city.yamagata-yamagata.lg.jp/shiseijoho/shicho/1006787/1011412/1008922.html',
          mapQuery: '蔵王温泉',
        },
      ],
    },
    {
      id: 'kamo',
      name: '加茂水族館',
      area: '鶴岡市・2月の夜',
      image: '/assets/scenery/yamagata/scenes/09_kamo.webp',
      hotspots: [
        {
          id: 'kamo-kurage',
          x: 0.4,
          y: 0.3,
          title: 'クラゲは100種類',
          body: 'クラゲの展示で知られる鶴岡の水族館です。2026年4月1日にリニューアルオープンし、展示するクラゲは100種類と世界最大級。通称は「東北エプソンアクアリウムかもすい」になりました。',
          source: 'https://www.tsuruokakanko.com/spot/281',
          relatedItemId: 'dadachamame',
          mapQuery: '鶴岡市立加茂水族館',
        },
        {
          id: 'kamo-saisei',
          x: 0.73,
          y: 0.24,
          title: '想定を超えた2014年',
          body: '2014年6月のリニューアルでは年間30万人の入館を想定していましたが、その年の入館者は71.6万人にのぼりました。その後も「多種類」のクラゲ展示を強みに、ほかの水族館との違いを打ち出しています。',
          source:
            'https://www.city.tsuruoka.lg.jp/shisei/koutyou/publiccomment/r01/kamosui.files/kamosui_keikaku.pdf',
          mapQuery: '加茂水族館 クラゲドリーム館',
        },
      ],
    },
    {
      id: 'shirakawa',
      name: '白川湖の水没林',
      area: '飯豊町・4月の夜明け',
      image: '/assets/scenery/yamagata/scenes/10_shirakawa.webp',
      hotspots: [
        {
          id: 'shirakawa-yukidoke',
          x: 0.3,
          y: 0.3,
          title: '雪どけの間だけ現れる林',
          body: '3月下旬から雪どけ水がダムに流れこみ、洪水にそなえて空けてある分まで水が満ちると、木々が水に浸かった林が現れます。見られるのは4月中旬から5月中旬ごろまで。水位は年によって変わります。',
          source: 'https://www.thr.mlit.go.jp/mogami/news/old-shirakawa/topics/2019/pdf/0416.pdf',
          mapQuery: '白川湖の水没林',
        },
        {
          id: 'shirakawa-yanagi',
          x: 0.84,
          y: 0.3,
          title: '水に立つシロヤナギ',
          body: '水に浸かって立っているのはシロヤナギです。早朝、木々に朝霧がかかると、物語の一場面のような景色になります。',
          source: 'https://www.town.iide.yamagata.jp/012/suiboturinn.html',
          mapQuery: '白川ダム湖岸公園',
        },
        {
          id: 'shirakawa-manner',
          x: 0.2,
          y: 0.45,
          title: '朝いちばんは静かに',
          body: '飯豊町は、県道への路上駐車をやめて公園内の駐車場を使うこと、早朝の撮影は静かにすることを呼びかけています。カヌーに乗るときはダム管理支所への申請が必要です。',
          source: 'https://www.town.iide.yamagata.jp/012/suiboturinn.html',
          mapQuery: '白川ダム 飯豊町',
        },
      ],
    },
  ],
};
